class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.algoliaService = new AlgoliaRecommendations();

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  async renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Add recommendations after rendering the main content
    this.loadRecommendations(parsedState);

    if (this.header) this.header.reveal();
    this.open();
  }

  async loadRecommendations(parsedState) {
    try {
      // Get the added item's variant ID
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const addedItem = cart.items.find(item => item.key === this.cartItemKey);
      
      if (!addedItem) return;

      // Get recommendations from Algolia
      const recommendations = await this.algoliaService.getBoughtTogether(addedItem.variant_id, 30);
      
      if (!recommendations || recommendations.length === 0) {
        console.log('No recommendations found');
        return;
      }

      // Filter out products already in cart
      const cartProductHandles = cart.items.map(item => item.handle);
      const filteredRecommendations = recommendations.filter(product => 
        !cartProductHandles.includes(product.handle)
      );

      if (filteredRecommendations.length === 0) return;

      // console.log('filter',filteredRecommendations)
      // Limit to 2 products
      const limitedProducts = filteredRecommendations.slice(0, 2);

      // console.log('limited', limitedProducts);
      // Render recommendations
      await this.renderRecommendations(limitedProducts);

    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  }

  async renderRecommendations(products) {
    try {
      // Find or create recommendations container
      let recommendationsContainer = this.notification.querySelector('[data-recommendations]');
      
      if (!recommendationsContainer) {
        // Create recommendations container
        const recommendationsHTML = `
          <div class="cart-notification__recommendations" data-recommendations>
            <h4 class="cart-notification__recommendations-title">You may also like</h4>
            <div class="cart-notification__recommendations-grid" data-recommendations-grid>
            </div>
          </div>
        `;
        
        // Insert before the product
        const linksSection = this.notification.querySelector('.cart-notification-product');
        if (linksSection) {
          linksSection.insertAdjacentHTML('beforebegin', recommendationsHTML);
          recommendationsContainer = this.notification.querySelector('[data-recommendations]');
        }
      }

      const recommendationsGrid = recommendationsContainer.querySelector('[data-recommendations-grid]');
      if (!recommendationsGrid) return;

      // Clear existing recommendations
      recommendationsGrid.innerHTML = '';

      // Fetch full product data from Shopify
      const productPromises = products.map(product => 
        fetch(`/products/${product.handle}.js`).then(res => res.json())
      );

      const fullProducts = await Promise.all(productPromises);

      // Render each product
      fullProducts.forEach(product => {
        const productHTML = this.createRecommendationHTML(product);
        recommendationsGrid.insertAdjacentHTML('beforeend', productHTML);
      });

      // Initialize click handlers
      this.initializeRecommendationHandlers(recommendationsGrid);

    } catch (error) {
      console.error('Error rendering recommendations:', error);
    }
  }

  createRecommendationHTML(product) {
    const hasVariants = product.variants.length > 1;
    const isAvailable = product.available;
    const buttonText = hasVariants ? 'Options' : (isAvailable ? '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.88327 1.52C6.88327 0.967712 6.43555 0.519997 5.88327 0.519997C5.33099 0.519997 4.88327 0.967712 4.88327 1.52V4.99998H1.4028C0.850517 4.99998 0.402802 5.44769 0.402802 5.99998C0.402802 6.55226 0.850517 6.99998 1.4028 6.99998H4.88327V10.48C4.88327 11.0323 5.33099 11.48 5.88327 11.48C6.43555 11.48 6.88327 11.0323 6.88327 10.48V6.99998H10.3628C10.9151 6.99998 11.3628 6.55226 11.3628 5.99998C11.3628 5.44769 10.9151 4.99998 10.3628 4.99998H6.88327V1.52Z" fill="white"></path></svg> Add' : 'Sold Out');
    const buttonClass = `cart-notification__recommendation-button ${!isAvailable ? 'disabled' : ''}`;
    
    return `
      <div class="cart-notification__recommendation" data-product-id="${product.id}">
        <div class="cart-notification_recommendation-image-button">
          <div class="cart-notification__recommendation-image">
            ${product.featured_image ? `
              <img src="${product.featured_image}" alt="${product.title}" loading="lazy">
            ` : ''}
          </div>
          <div class="quick-add no-js-hidden">
              ${hasVariants ? `
                  <modal-opener data-modal="#QuickAdd-${product.id}">
                      <button
                          id="quick-add-${product.id}-submit"
                          type="submit"
                          name="add"
                          class="${buttonClass} quick-add__submit button button--full-width button"
                          aria-haspopup="dialog"
                          aria-labelledby="quick-add-${product.id}-submit title-${product.id}"
                          data-product-url="/products/${product.handle}"
                          data-product-id="${product.id}"
                      >
                          ${buttonText}
                          <div class="loading__spinner hidden">
                              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">
                                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                              </svg>
                          </div>
                      </button>
                  </modal-opener>
                  <quick-add-modal id="QuickAdd-${product.id}" class="quick-add-modal">
                      <div
                          role="dialog"
                          aria-label="{{ 'products.product.choose_product_options' | t: product_name: product.title }}"
                          aria-modal="true"
                          class="quick-add-modal__content global-settings-popup"
                          tabindex="-1"
                      >
                          <button
                              id="ModalClose-${product.id}"
                              type="button"
                              class="quick-add-modal__toggle"
                              aria-label="{{ 'accessibility.close' | t }}"
                          >
                              <svg aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13">
                                  <path fill="currentColor" fill-rule="evenodd" d="M5.306 6.5L0 1.194 1.194 0 6.5 5.306 11.806 0 13 1.194 7.694 6.5 13 11.806 11.806 13 6.5 7.694 1.194 13 0 11.806 5.306 6.5z"></path>
                              </svg>
                          </button>
                          <div id="QuickAddInfo-${product.id}" class="quick-add-modal__content-info"></div>
                      </div>
                  </quick-add-modal>
              ` : `
                  <algolia-product-form data-section-id="algolia-${product.id}">
                      <form action="/cart/add" method="post" enctype="multipart/form-data" class="form" novalidate="novalidate" data-type="add-to-cart-form" id="product form-${product.id}">
                          <input type="hidden" name="id" value="${product.variants?.[0]?.id || product.objectID}" class="product-variant-id" />
                          <button
                              id="ProductSubmitButton-${product.id}"
                              type="submit"
                              name="add"
                              class="${buttonClass} quick-add__submit button button--full-width button"
                              aria-labelledby="ProductSubmitButton-${product.id} title-${product.id}"
                              aria-live="polite"
                              data-sold-out-message="true"
                          >
                              ${buttonText}
                              <span class="sold-out-message hidden">{{ 'products.product.sold_out' | t }}</span>
                              <div class="loading__spinner hidden">
                                  <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">
                                      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                                  </svg>
                              </div>
                          </button>
                      </form>
                  </algolia-product-form>
              `}
          </div>
        </div>
        <div class="cart-notification__recommendation-info">
          <h5 class="cart-notification__recommendation-title"><a href="https://medicalrite.com/products/${product.handle}">${product.title}${this.getSoldByOption(product) ? `, <span>${this.getSoldByOption(product)}</span>` : ''}</a></h5>
          <div class="cart-notification__recommendation-price">
            <div class="price-wrap-custom">
              ${hasVariants ? `
              <span class="from-span">From</span>
              ` : ''}
              <span class="productitem--price">
                <span class="productitem--price-main${product.compare_at_price > product.price ? ' price--reduced' : ''}">
                  ${Shopify.formatMoney(product.price)}
                </span>
                ${product.compare_at_price > product.price ? `
                  <span class="productitem--price-compare-at">
                    ${Shopify.formatMoney(product.compare_at_price)}
                  </span>
                ` : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  getSoldByOption(product) {
    // console.log(product);
    if (!product.options) return '';
    
    for (let option of product.options) {
      const optionNameHandle = option.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      // console.log(optionNameHandle);
      if (optionNameHandle === 'sold-by') {
        // console.log(option);
        const selectedValue = option.values[0];
        const selectedValueHandle = selectedValue.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        
        // Don't return anything for these specific values
        if (['each', 'ea'].includes(selectedValueHandle)) {
          return '';
        }
        
        return selectedValue;
      }
    }
    
    return '';
  }

  initializeRecommendationHandlers(container) {
    const buttons = container.querySelectorAll('.cart-notification__recommendation-button');
    
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-shipping', // Add this line
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
